/* Free miniature — the rule, and the picker that goes with it.

   Two halves in one file, because they are two views of the same state:

   1. THE RULE, which runs wherever the bag might need putting right. A
      free line cannot sit in the bag on its own: take the qualifying
      product out and the miniature goes with it. Only one free line is
      ever held, at a quantity of one. This cannot live on the product
      page alone — the cart page removes a line by following a link and
      reloading, and the drawer removes one with no product page in sight.

   2. THE PICKER (snippets/pdp-gift-miniature.liquid), on an eligible
      product page: the row under the buy buttons and the sheet/dialog it
      opens.

   WHAT IT COSTS A PAGE. Nothing it does not have to.

   layout/theme.liquid works the bag out in Liquid and hands it over in a
   JSON tag — which lines are free, whether anything pays for one, how many
   of each. So the usual page, whose bag is already right, makes NO network
   request on load: this file reads that config, agrees there is nothing to
   do, and stops. A bag that does need putting right costs exactly one
   POST, with no GET before it. The Cart API is only ever read after a real
   cart change or when the shopper opens the picker — never in the critical
   path of a page load. The same Liquid decides not to load this file at
   all on pages that can neither offer a gift nor be holding one.

   The picker's first paint is server-rendered too: the row and every
   button come out of Liquid already in the right state, so nothing moves
   after load.

   WHAT COUNTS AS A FREE LINE. Either the `_free_gift` property this file
   writes on every gift it adds, or a line that costs nothing. The second
   is what makes the rule hold whatever route the miniature came in by —
   the miniatures are priced at zero in the admin, so one added from
   anywhere else in the shop is still recognised and still cannot travel to
   checkout alone.

   WHAT PAYS FOR IT. Decided by Liquid, never guessed here. The offer is
   the shop metafield vro.gift_config (written by the admin dashboard's Free
   Miniature page), and only Liquid can see a product's tags and
   collections, so layout/theme.liquid hands over the bag already worked
   out (snippets/gwp-bag.liquid: which lines qualify, what they come to,
   whether the offer is open) and, after a cart change, this file asks
   sections/gwp-bag.liquid for the same account again. The one thing done
   in the browser is the sum when a line is about to be removed
   (companionUpdates), from the per-line answers Liquid gave.

   Cart changes arrive as `vro:cart-updated`, dispatched by
   window.updateCartCount in assets/custom.js — the one function every add
   path, the drawer and the cart page's own refresh already call. */
(function () {
  var PENDING_KEY = 'vro:gift:pending';

  function readConfig() {
    var el = document.getElementById('vro-gift-config');
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  var CFG = readConfig();
  if (!CFG) return;

  var REMOVED_TOAST = CFG.removedToast || 'Free miniature removed — your bag no longer has a product it comes with';

  /* What the bag looks like right now. Seeded from the server's account of
     it (CFG.bag), replaced by a fresh one once there has been a reason to
     ask. */
  var state = {
    giftKeys: [],
    giftVariants: [],
    giftQtys: [],
    qualifies: false,
    open: false,
    value: 0,
    qty: 0,
    minValue: 0,
    minQty: 1,
    /* Every line in the bag — key, free, whether it qualifies, what it is
       worth — but only when a gift is in it. This is what
       companionUpdates() reads to take the gift out in the same request as
       whatever was removed. */
    lines: []
  };

  function setFromBag(bag) {
    bag = bag || {};
    state.lines = (bag.lines || []).map(function (l) {
      return {
        key: String(l.key),
        variant: String(l.variant),
        free: !!l.free,
        elig: !!l.elig,
        val: Number(l.val) || 0,
        qty: Number(l.qty) || 0
      };
    });
    var gifts = state.lines.filter(function (l) { return l.free; });
    state.giftKeys = gifts.map(function (l) { return l.key; });
    state.giftVariants = gifts.map(function (l) { return l.variant; });
    state.giftQtys = gifts.map(function (l) { return l.qty; });
    state.qualifies = !!bag.qualifies;
    state.open = !!bag.open;
    state.value = Number(bag.value) || 0;
    state.qty = Number(bag.qty) || 0;
    state.minValue = Number(bag.minValue) || 0;
    state.minQty = Number(bag.minQty) || 1;
  }
  setFromBag(CFG.bag);

  var cart = null;      // only ever set after a real read
  var busy = false;
  var picker = null;

  /* ---------------- cart helpers ---------------- */

  /* The offer's account of the bag, from Liquid: sections/gwp-bag.liquid
     prints one JSON object. Asked fresh every time (a timestamp and
     no-store), since an answer from before the change is the one thing
     that must never be reused. */
  function evalBag() {
    return fetch('/?section_id=gwp-bag&t=' + Date.now(), { headers: { Accept: 'text/html' }, cache: 'no-store' })
      .then(function (r) {
        if (!r.ok) throw new Error('Bag check failed');
        return r.text();
      })
      .then(function (text) {
        var m = text.match(/\{[\s\S]*\}/);
        if (!m) throw new Error('Bag check unreadable');
        return JSON.parse(m[0]);
      });
  }

  function fetchCart() {
    return Promise.all([
      fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); }),
      evalBag()
    ]).then(function (res) { adopt(res[0], res[1]); return res[0]; });
  }

  function isFreeLine(line) {
    if (line.properties && line.properties._free_gift) return true;
    /* final_price is what the line actually costs after any discount —
       the honest test of "this line pays for nothing". */
    return line.final_price === 0;
  }

  /* Re-derive the state from a real cart and the offer's account of it, so
     both halves read one model whether it came from Liquid on load or from
     the two requests after a change. */
  function adopt(json, bag) {
    cart = json;
    setFromBag(bag);
  }

  /* /cart/update.js keyed by each line's own key, not /cart/change.js —
     see the note in assets/cart-panel.js: change.js's line-position
     removal zeroes out every other line in this store's cart. */
  function updateLines(updates) {
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates: updates })
    })
      .then(function (r) {
        /* An error body has no `items`, and adopting it would read as an
           empty bag — which is the one conclusion that unlocks nothing and
           removes everything. Refuse it instead. */
        if (!r.ok) throw new Error('Cart update failed');
        return r.json();
      })
      .then(function (json) {
        return evalBag().then(function (bag) { adopt(json, bag); return json; });
      });
  }

  function addVariant(variantId) {
    var body = new FormData();
    body.append('id', variantId);
    body.append('quantity', 1);
    /* What the rule looks for, and what an automatic discount can target. */
    body.append('properties[_free_gift]', 'yes');
    return fetch('/cart/add.js', { method: 'POST', body: body, headers: { Accept: 'application/json' } })
      .then(function (res) {
        if (!res.ok) throw new Error('Gift add failed');
        return res.json();
      });
  }

  /* Tell the rest of the theme, the way every add path does: the header
     badge follows window.updateCartCount, and the drawer can only be
     redrawn from its own server-rendered markup. */
  function announce() {
    if (!cart) return Promise.resolve();
    if (window.updateCartCount) window.updateCartCount(cart.item_count);
    if (window.VroCartPanel && window.VroCartPanel.refresh) {
      /* The panel re-renders from the server; handing the promise back lets
         a caller open it holding the new cart rather than the old one. */
      return Promise.resolve(window.VroCartPanel.refresh()).catch(function () {});
    }
    return Promise.resolve();
  }

  function pendingVariant() {
    try { return window.localStorage.getItem(PENDING_KEY); } catch (e) { return null; }
  }
  function setPending(variantId) {
    try {
      if (variantId) window.localStorage.setItem(PENDING_KEY, String(variantId));
      else window.localStorage.removeItem(PENDING_KEY);
    } catch (e) { /* private mode — the claim just does not survive the page */ }
  }

  /* ---------------- the rule ---------------- */

  /* What the bag needs doing to it, as an updates map — worked out from
     `state`, which may have come from Liquid without a single request. */
  function correction() {
    if (!state.giftKeys.length) return null;

    var updates = {};
    if (!state.qualifies) {
      state.giftKeys.forEach(function (key) { updates[key] = 0; });
      return { updates: updates, removedAll: true };
    }

    /* One, and one of it. Whatever put a second one there — another tab, a
       saved bag, a card's own (+) — the bag keeps the first. */
    state.giftKeys.forEach(function (key, i) {
      if (i > 0) updates[key] = 0;
      else if (state.giftQtys[i] > 1) updates[key] = 1;
    });

    return Object.keys(updates).length ? { updates: updates, removedAll: false } : null;
  }

  /* THE ATOMIC HALF OF THE RULE.

     Given the updates someone is about to send — the drawer removing a
     line, the cart page removing one — work out whether the bag would
     still pay for the gift afterwards, and if not, put the gift's own
     lines into the SAME map. One request, one render: the parent and the
     miniature leave together, and there is no moment in between where the
     bag holds a free line on its own and Checkout is reachable.

     Mutates and returns the map it is given, so a caller can simply hand
     over what it was about to post. */
  function companionUpdates(updates) {
    if (!updates || !state.giftKeys.length || !state.lines.length) return updates;

    /* The bag as it will be: a line set to 0 is gone, a line set to
       another quantity is worth that many of itself, the rest stay. Each
       line's own answer to "does it qualify" is Liquid's, from when the
       bag was last worked out. */
    var qualValue = 0;
    var qualQty = 0;
    state.lines.forEach(function (line) {
      if (line.free || !line.elig) return;
      var qty = line.qty;
      if (Object.prototype.hasOwnProperty.call(updates, line.key)) qty = Number(updates[line.key]);
      if (!(qty > 0)) return;
      qualQty += qty;
      qualValue += line.qty > 0 ? Math.round(line.val / line.qty * qty) : 0;
    });

    var willQualify = state.open && qualQty > 0 && qualQty >= state.minQty && qualValue >= state.minValue;

    if (!willQualify) {
      state.giftKeys.forEach(function (key) { updates[key] = 0; });
    }

    return updates;
  }

  /* Checkout, locked while the bag is being written to. Set on <html> so it
     survives the drawer replacing its own footer markup on every refresh,
     which a class on the button itself would not.

     Counted, not a flag: the drawer and this file can both be mid-write at
     the same time (a removal in the panel while a swap is still settling),
     and whichever finished first would otherwise unlock the button for the
     one still going. Every lock is paired with an unlock in a finally-style
     step, so the count comes back to nought even when a request fails. */
  var cartBusyDepth = 0;
  function setCartBusy(isBusy) {
    if (isBusy) cartBusyDepth += 1;
    else cartBusyDepth = Math.max(0, cartBusyDepth - 1);
    document.documentElement.classList.toggle('vro-cart-busy', cartBusyDepth > 0);
  }

  /* THE LAST LOOK BEFORE CHECKOUT.

     Reads the bag fresh, puts it right if a free miniature is in it with
     nothing to pay for it, and says whether it is safe to hand the shopper
     on: { proceed: true } or { proceed: false }.

     It exists because everything else here is about keeping the bag right
     as it changes, and checkout is the one moment that has to be right no
     matter how it got that way — a stale page, a second tab, a link that
     added a miniature straight to the bag. Called by the checkout buttons
     below, and by Buy Now (assets/custom.js), which skips the bag entirely
     and goes straight to checkout.

     Not proceeding is the safe answer to every doubt: a bag left empty by
     the correction has nothing to check out with, and a bag that could not
     be verified while it holds a free item is not waved through. */
  function guardCheckout() {
    setCartBusy(true);
    var failed = false;

    return fetchCart()
      .then(function () {
        var fix = correction();
        if (!fix) return null;
        return updateLines(fix.updates).then(function () {
          if (fix.removedAll && window.showToast) {
            window.showToast(REMOVED_TOAST);
          }
          return announce();
        });
      })
      .catch(function () { failed = true; })
      .then(function () {
        setCartBusy(false);
        var empty = !!cart && cart.item_count === 0;
        var stillWrong = !!correction();
        /* Could not read the bag: only trust it if nothing free was known
           to be in it. */
        var unverified = failed && state.giftKeys.length > 0;
        return { proceed: !empty && !stillWrong && !unverified };
      });
  }

  var guardingCheckout = false;

  /* The checkout buttons: the drawer's link and the cart page's submit.

     While the bag is being written to they do nothing — pointer-events in
     CSS stops the mouse, this stops the keyboard and anything that fires a
     click programmatically. And when a free miniature is in the bag, the
     click is held for the one fresh look above before the shopper goes on.
     With no miniature in it there is nothing to hold, so the ordinary
     checkout click is not slowed at all. */
  document.addEventListener('click', function (e) {
    var target = e.target.closest('.cart-panel-checkout-btn, [name="checkout"], [href="/checkout"]');
    if (!target) return;

    if (document.documentElement.classList.contains('vro-cart-busy') || guardingCheckout) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    if (!state.giftKeys.length) return;

    e.preventDefault();
    e.stopPropagation();
    guardingCheckout = true;

    guardCheckout().then(function (result) {
      guardingCheckout = false;
      if (!result.proceed) {
        if (CFG.template === 'cart') window.location.reload();
        return;
      }
      var form = target.closest('form');
      if (target.tagName === 'A') {
        window.location.href = target.getAttribute('href') || '/checkout';
      } else if (form && form.requestSubmit) {
        form.requestSubmit(target);
      } else if (form) {
        form.submit();
      }
    });
  }, true);

  var corrected = false;

  function applyCorrection() {
    var fix = correction();
    if (!fix) return null;

    return updateLines(fix.updates).then(function () {
      corrected = true;
      announce();
      if (fix.removedAll && window.showToast) {
        window.showToast(REMOVED_TOAST);
      }
    });
  }

  /* A claim taken before the bag qualified, now that it does. */
  function redeemPending() {
    var pending = pendingVariant();
    if (!pending || !state.qualifies || state.giftKeys.length) return null;

    return addVariant(pending)
      .then(fetchCart)
      .then(function () {
        setPending(null);
        announce();
        if (window.showToast) window.showToast('Your free miniature has been added', 'cart');
      })
      .catch(function () {
        /* Gone while it waited — drop the claim rather than retrying it on
           every cart change from here on. */
        setPending(null);
      });
  }

  /* On load: no read of the cart at all. Liquid has already told us what
     is in it, so the only request ever made here is the one that puts a
     wrong bag right. */
  function settleFromServerState() {
    if (busy) return Promise.resolve();
    busy = true;
    corrected = false;
    setCartBusy(true);

    return Promise.resolve()
      .then(function () { return redeemPending(); })
      .then(function () { return applyCorrection(); })
      .catch(function () {})
      .then(function () {
        busy = false;
        setCartBusy(false);
        /* The correction could not be made — no network, a refused update.
           The bag is still wrong, so Checkout stays locked rather than
           being released just because this attempt is over. The next cart
           change tries again. */
        if (correction() && !corrected) document.documentElement.classList.add('vro-cart-busy');
        if (picker) picker.render();
        /* The cart page is server-rendered and has no way to redraw one
           line, so a bag put right here is shown by drawing it again. */
        if (corrected && CFG.template === 'cart') window.location.reload();
      });
  }

  /* After a real cart change, where the server's account is stale. */
  function settleFromCart() {
    if (busy) return Promise.resolve();
    busy = true;
    corrected = false;
    setCartBusy(true);

    return fetchCart()
      .then(function () { return redeemPending(); })
      .then(function () { return applyCorrection(); })
      .catch(function () {})
      .then(function () {
        busy = false;
        setCartBusy(false);
        if (picker) picker.render();
        if (corrected && CFG.template === 'cart') window.location.reload();
      });
  }

  /* ---------------- the picker ---------------- */

  /* No miniature can be offered right now (all out of stock, or all with
     options), so there is nothing to pick or keep in step — but the row
     is still on the page, and a row that does nothing when tapped reads
     as broken. It opens the sheet, which says the miniatures are claimed
     for now, and closes again the usual ways. */
  function initEmptyPicker(sheet, row) {
    var lastFocus = null;
    var moved = false;

    function open() {
      lastFocus = document.activeElement;
      if (!moved) { document.body.appendChild(sheet); moved = true; }
      sheet.hidden = false;
      document.body.style.overflow = 'hidden';
      var close = sheet.querySelector('.gift-sheet-close');
      if (close) close.focus();
    }
    function close() {
      sheet.hidden = true;
      document.body.style.overflow = '';
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }

    row.addEventListener('click', open);
    sheet.addEventListener('click', function (e) {
      if (e.target.closest('[data-gift-close]')) close();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !sheet.hidden) close();
    });
    return null;
  }

  function initPicker() {
    var sheet = document.querySelector('[data-gift-sheet]');
    var row = document.querySelector('[data-gift-open]');
    if (!sheet || !row) return null;

    var items = Array.prototype.slice.call(sheet.querySelectorAll('[data-gift-item]'));
    if (!items.length) return initEmptyPicker(sheet, row);

    var mode = sheet.getAttribute('data-gift-mode') || 'gate';
    var productId = String(sheet.getAttribute('data-gift-product-id') || '');
    /* Liquid's answer to "is the product this page is about already in the
       bag", so the first paint needs no cart read. */
    var parentInCart = sheet.getAttribute('data-gift-parent-in-cart') === 'true';
    var note = sheet.querySelector('[data-gift-note]');
    var rowPicked = row.querySelector('[data-gift-row-picked]');
    var rowOffer = row.querySelector('[data-gift-row-offer]');
    var lastFocus = null;
    var moved = false;

    /* Whether the bag qualifies is the offer's own answer (minimum value,
       minimum quantity, which products count) — not "is this page's
       product in the bag", since that product may not be enough on its
       own. */
    function unlocked() {
      return state.qualifies;
    }

    /* The words on the buttons are the dashboard's, handed over on the
       sheet. */
    function label(name, fallback) {
      return sheet.getAttribute('data-btn-' + name) || fallback;
    }

    /* {remaining}, {min_value} and {min_qty} in a note: what is still
       needed, worked out from the bag as it stands. */
    function fillNote(text) {
      var remaining = Math.max(0, Math.ceil((state.minValue - state.value) / 100));
      return String(text)
        .split('{remaining}').join(String(remaining))
        .split('{min_value}').join(String(Math.round(state.minValue / 100)))
        .split('{min_qty}').join(String(state.minQty));
    }

    function heldVariant() {
      return state.giftVariants.length ? state.giftVariants[0] : null;
    }

    var parentThumb = document.getElementById('giftParentThumb');
    var parentVariantEl = document.getElementById('giftParentVariant');
    var parentPriceEl = document.getElementById('giftParentPrice');
    var parentCompareEl = document.getElementById('giftParentCompare');
    var parentOffEl = document.getElementById('giftParentOff');

    /* The image Liquid rendered, kept as the fallback: not every variant
       has one of its own in Shopify, and a variant without an image should
       show the product's rather than nothing. */
    var parentFallbackImg = parentThumb ? parentThumb.getAttribute('src') : '';

    /* The product's variants, straight from the JSON the buy form already
       prints (#ProductVariantsJson). The picker reads that rather than
       depending on another file to push values into it — which is what it
       did at first, and why the row sat on whatever Liquid had rendered
       while the page's own price and picker moved on without it. */
    var productVariants = [];
    try {
      var variantsEl = document.getElementById('ProductVariantsJson');
      if (variantsEl) productVariants = JSON.parse(variantsEl.textContent) || [];
    } catch (e) { productVariants = []; }

    function money(cents) {
      return '₹' + (cents / 100).toLocaleString('en-IN', { maximumFractionDigits: 0 });
    }

    /* Which variant the page is on right now. #ProductVariantId is what the
       buy form actually submits, so it is the one true answer — the same
       value that makes the right thing land in the bag. */
    function currentVariant() {
      var idInput = document.getElementById('ProductVariantId');
      var id = idInput ? String(idInput.value) : '';
      if (!id) return null;
      for (var i = 0; i < productVariants.length; i++) {
        if (String(productVariants[i].id) === id) return productVariants[i];
      }
      return null;
    }

    /* The row, brought up to the variant on screen: image, which one it is,
       price, and whether it can be added at all. */
    function syncParent() {
      var v = currentVariant();
      if (!v) return;

      if (parentThumb) {
        var src = (v.featured_image && v.featured_image.src) || '';
        if (src) {
          parentThumb.src = src + (src.indexOf('?') > -1 ? '&' : '?') + 'width=160';
        } else if (parentFallbackImg) {
          parentThumb.src = parentFallbackImg;
        }
      }

      if (parentVariantEl && v.title && v.title !== 'Default Title') {
        parentVariantEl.textContent = String(v.title).split(' / ').join(' • ');
      }

      if (parentPriceEl) parentPriceEl.textContent = money(v.price);

      if (parentCompareEl && parentOffEl) {
        if (v.compare_at_price && v.compare_at_price > v.price) {
          parentCompareEl.textContent = money(v.compare_at_price);
          parentCompareEl.hidden = false;
          parentOffEl.textContent = Math.round((v.compare_at_price - v.price) * 100 / v.compare_at_price) + '% off';
          parentOffEl.hidden = false;
        } else {
          parentCompareEl.hidden = true;
          parentOffEl.hidden = true;
        }
      }
    }

    /* Delegated, and a tick late on purpose: the theme's own handler is
       bound straight to each option input, so it runs first and writes the
       new id into #ProductVariantId — which is what this then reads. */
    document.addEventListener('change', function (e) {
      var input = e.target;
      if (!input || !input.classList || !input.classList.contains('option-swatch')) return;
      window.setTimeout(syncParent, 0);
    });

    /* Is this page's product — the variant on screen — already in the bag? */
    function parentVariantInCart() {
      var idInput = document.getElementById('ProductVariantId');
      var variantId = idInput ? String(idInput.value) : '';
      if (!variantId || !cart || !cart.items) return null;
      return cart.items.some(function (line) {
        return String(line.variant_id) === variantId;
      });
    }

    /* Does taking a miniature have to bring this page's product with it?
       Before any cart has been read, Liquid's answer stands: a null from
       parentVariantInCart means exactly that — not "no". */
    function needsParent() {
      var inBag = parentVariantInCart();
      if (inBag === null) inBag = parentInCart;
      return !inBag;
    }

    function render() {
      var isUnlocked = unlocked();

      syncParent();

      var held = heldVariant();
      var pending = pendingVariant();
      var heldTitle = '';

      sheet.classList.toggle('is-unlocked', isUnlocked);

      if (note) {
        var text = note.getAttribute(isUnlocked ? 'data-note-unlocked' : 'data-note-locked');
        if (text) note.textContent = fillNote(text);
      }

      items.forEach(function (item) {
        var variantId = String(item.getAttribute('data-gift-variant'));
        var btn = item.querySelector('[data-gift-add]');
        if (!btn) return;

        var isHeld = held === variantId;
        var isPending = !isHeld && !isUnlocked && pending === variantId;
        var titleEl = item.querySelector('.gift-item-title');

        btn.classList.toggle('is-picked', isHeld);
        btn.classList.toggle('is-pending', isPending);
        item.classList.toggle('is-picked', isHeld);

        if (isHeld) {
          btn.disabled = false;
          btn.textContent = label('added', 'Added ✓');
          heldTitle = titleEl ? titleEl.textContent.trim() : '';
        } else if (isPending) {
          btn.disabled = false;
          btn.textContent = label('claimed', 'Claimed');
        } else if (isUnlocked) {
          btn.disabled = false;
          btn.textContent = held ? label('swap', 'Swap') : label('add', 'Add');
        } else {
          /* The bag does not qualify yet. Tapping a miniature adds this
             page's product first (takeGift), so the button is live; only
             when that product is already in the bag and still not enough
             does a claim-mode shop show "Claim" (the choice is kept). */
          btn.disabled = false;
          btn.textContent = (mode === 'claim' && !needsParent()) ? label('claim', 'Claim') : label('add', 'Add');
        }
      });

      /* Only ever the gift actually in the bag, which Liquid has already
         drawn on the way out — so this never inserts a line of text after
         load and never moves the page under anyone. A claim that has not
         been redeemed yet is shown inside the sheet, not out here. */
      var holding = !!(held && heldTitle);

      if (rowPicked) {
        if (holding) {
          rowPicked.textContent = 'Free gift added: ' + heldTitle;
          rowPicked.hidden = false;
        } else {
          rowPicked.hidden = true;
          rowPicked.textContent = '';
        }
      }

      /* The offer steps aside once it has been taken, and comes back if the
         miniature is given back or removed from the bag. */
      if (rowOffer) rowOffer.hidden = holding;
    }

    /* The button that was tapped, spinning, with the rest of the list
       frozen — so a shopper cannot start a second swap while the first is
       still in flight, and can see which one they asked for. */
    function beginWork(variantId) {
      sheet.classList.add('is-busy');
      items.forEach(function (item) {
        var btn = item.querySelector('[data-gift-add]');
        if (!btn) return;
        var isTarget = String(item.getAttribute('data-gift-variant')) === String(variantId);
        btn.classList.toggle('is-loading', isTarget);
        if (isTarget) btn.setAttribute('aria-busy', 'true');
        btn.disabled = true;
      });
    }

    function endWork() {
      sheet.classList.remove('is-busy');
      items.forEach(function (item) {
        var btn = item.querySelector('[data-gift-add]');
        if (!btn) return;
        btn.classList.remove('is-loading');
        btn.removeAttribute('aria-busy');
      });
      render();
    }

    /* Straight to the bag once a miniature is taken: the drawer, refreshed,
       with the gift in it. announce() has already redrawn it, so this only
       has to show it — and the sheet closes first, since the choice that
       opened it has been made. */
    function showCart() {
      closeSheet();
      if (window.VroCartPanel && window.VroCartPanel.open) window.VroCartPanel.open();
    }

    function takeGift(variantId) {
      if (busy) return;

      if (!unlocked()) {
        /* The product comes with the miniature: add it, then the gift. */
        if (needsParent()) { addParentThenGift(variantId); return; }

        /* The product is already in the bag and is still not enough. In
           claim mode the choice is simply remembered; otherwise there is
           nothing to take yet. */
        if (mode !== 'claim') { tellShortfall(); return; }
        setPending(pendingVariant() === variantId ? null : variantId);
        render();
        return;
      }

      var held = heldVariant();
      var heldKey = state.giftKeys.length ? state.giftKeys[0] : null;
      busy = true;

      /* Tapping the one already held gives it back. */
      if (held === variantId && heldKey) {
        var drop = {};
        drop[heldKey] = 0;
        beginWork(variantId);
        setCartBusy(true);
        updateLines(drop)
          .then(announce)
          .catch(function () {})
          .then(function () { busy = false; setCartBusy(false); endWork(); });
        return;
      }

      /* Swapping: the one in the bag leaves before the new one arrives, so
         the bag never briefly holds two free lines. */
      beginWork(variantId);
      setCartBusy(true);

      var start;
      if (heldKey) {
        var swap = {};
        swap[heldKey] = 0;
        start = updateLines(swap);
      } else {
        start = Promise.resolve();
      }

      var added = false;
      start
        .then(function () { return addVariant(variantId); })
        .then(fetchCart)
        .then(function () {
          added = true;
          setPending(null);
          if (window.showCartToast) window.showCartToast(cart, variantId);
          return announce();
        })
        .catch(function () {
          if (window.showToast) window.showToast('Could not add that miniature — try again');
        })
        .then(function () {
          busy = false;
          setCartBusy(false);
          endWork();
          if (added) showCart();
        });
    }

    /* What to say when the product is in the bag and the offer still wants
       more of it (a minimum value or quantity the product alone does not
       reach). */
    function tellShortfall() {
      if (!window.showToast) return;
      var short = Math.max(0, Math.ceil((state.minValue - state.value) / 100));
      window.showToast(short > 0
        ? 'Add \u20B9' + short.toLocaleString('en-IN') + ' more to get your free miniature'
        : 'Add a little more to your bag to get your free miniature');
    }

    /* Taking a miniature before its product is in the bag: the product goes
       in first, then the miniature, in one tap. The product is added with
       the buy form's own data (variant on screen, quantity, properties) —
       the same body the page's Add to cart sends — and awaited, so the
       miniature is only added once the bag has been re-read and the offer
       says it now qualifies. If the product alone does not reach the
       offer's minimum, it stays in the bag and the shopper is told what is
       missing; in claim mode the choice is remembered too. */
    function addParentThenGift(variantId) {
      var buyForm = document.getElementById('AddToCartForm');
      if (!buyForm) return;

      var v = currentVariant();
      if (v && v.available === false) {
        if (window.showToast) window.showToast('This product is sold out right now');
        return;
      }

      busy = true;
      beginWork(variantId);
      setCartBusy(true);

      var added = false;
      fetch('/cart/add.js', { method: 'POST', body: new FormData(buyForm), headers: { Accept: 'application/json' } })
        .then(function (res) {
          if (!res.ok) throw new Error('Add failed');
          return res.json();
        })
        .then(fetchCart)
        .then(function () {
          if (!unlocked()) {
            if (mode === 'claim') setPending(variantId);
            tellShortfall();
            return announce();
          }
          return addVariant(variantId)
            .then(fetchCart)
            .then(function () {
              added = true;
              setPending(null);
              if (window.showCartToast) window.showCartToast(cart, variantId);
              return announce();
            });
        })
        .catch(function () {
          if (window.showToast) window.showToast('Could not add that — try again');
        })
        .then(function () {
          busy = false;
          setCartBusy(false);
          endWork();
          if (added) showCart();
        });
    }

    function openSheet() {
      lastFocus = document.activeElement;

      /* Moved to the end of <body>, but not before it is needed. The sheet
         is position:fixed, and a fixed element inside an ancestor carrying
         a transform, filter or perspective is positioned against THAT
         ancestor rather than the viewport — which on a product page would
         pin a full-screen sheet inside the buy column. Done on first open
         rather than on load so a page nobody opens the sheet on pays
         nothing for it. */
      if (!moved) { document.body.appendChild(sheet); moved = true; }

      sheet.hidden = false;
      document.body.style.overflow = 'hidden';
      var close = sheet.querySelector('.gift-sheet-close');
      if (close) close.focus();

      /* A read here is free: it is a tap, not a page load, and it catches
         a bag changed in another tab since this page was drawn. */
      settleFromCart();
    }

    function closeSheet() {
      sheet.hidden = true;
      document.body.style.overflow = '';
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }

    row.addEventListener('click', openSheet);

    sheet.addEventListener('click', function (e) {
      if (e.target.closest('[data-gift-close]')) { closeSheet(); return; }

      var btn = e.target.closest('[data-gift-add]');
      if (!btn || btn.disabled) return;
      var item = btn.closest('[data-gift-item]');
      if (item) takeGift(String(item.getAttribute('data-gift-variant')));
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !sheet.hidden) closeSheet();
    });

    return { render: render };
  }

  /* ---------------- wiring ---------------- */

  /* The cart page removes a line by following a link, so the gift would
     otherwise only catch up after the reload — the page would come back
     still showing it, and Checkout would be live beside it. Intercepted
     only while a gift is actually in the bag: the same one request takes
     both lines out, then the page is drawn from the corrected cart.
     Without JS, the link still works the way Shopify wrote it. */
  function interceptCartPageRemoval() {
    if (!state.giftKeys.length) return;

    document.addEventListener('click', function (e) {
      var link = e.target.closest('[data-cart-remove-key]');
      if (!link || busy) return;

      var key = link.getAttribute('data-cart-remove-key');
      if (!key) return;

      var updates = {};
      updates[key] = 0;
      companionUpdates(updates);

      /* Nothing to fold in — let the link do what it always did. */
      if (Object.keys(updates).length < 2) return;

      e.preventDefault();
      busy = true;
      setCartBusy(true);
      link.style.opacity = '.5';

      updateLines(updates)
        .then(function () { window.location.reload(); })
        .catch(function () {
          busy = false;
          setCartBusy(false);
          link.style.opacity = '';
          /* Fall back to the plain link rather than stranding the row. */
          window.location.href = link.getAttribute('href');
        });
    });
  }

  function start() {
    picker = initPicker();
    interceptCartPageRemoval();

    /* Every add, removal and quantity change in the theme lands here. */
    document.addEventListener('vro:cart-updated', function () {
      if (busy) return;
      settleFromCart();
    });

    /* And the bag as the server drew it, which is what catches the cart
       page: removing a line there follows a link and reloads, so the bag
       is put right as the next page is drawn — without reading it again.
       Left to idle time, since nothing here is worth delaying a page for. */
    var settle = function () { settleFromServerState(); };

    /* Idle time is for a bag that is already right. One that is not — a
       free item with nothing paying for it — has Checkout locked from the
       first paint (layout/theme.liquid), and every moment spent waiting for
       the browser to be idle is a moment the shopper is locked out for
       nothing. */
    if (correction() || pendingVariant()) settle();
    else if (window.requestIdleCallback) window.requestIdleCallback(settle, { timeout: 2000 });
    else setTimeout(settle, 0);
  }

  /* What the rest of the theme calls into. assets/cart-panel.js folds
     companionUpdates() into its own removals, so the gift goes in the same
     request; both it and this file raise the same checkout lock. Every
     caller checks the global exists first, since this file is not loaded
     on a page that cannot have a gift. */
  window.VroGift = {
    companionUpdates: companionUpdates,
    setCartBusy: setCartBusy,
    guardCheckout: guardCheckout
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
